<template>
  <div class="dealer-profile-wrapper">
    <div class="d-flex justify-content-between align-items-center">
      <h4>Dealer Profile</h4>
      <h5><span v-if="!adminView" :class="['badge', dealerStatusStyle ]">{{ dealerStatus }}</span></h5>
      <BasicDropdown v-if="adminView" :key="dealer.DealerStatus" :items="dealerStatusOptions"  v-model="dealer.DealerStatus" />
    </div>
    <hr> 
    <fieldset >
      <div class="dealer-profile">
        <div class="dealer-profile__card card card-1 shadow-sm">
          <fieldset :disabled="adminView || !can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">General Information</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Company Name" label="Company Name" v-model="dealer.CompanyName"/>
                  </div>
                  <div class="col">
                    <BasicTextInput placeholder="Company Code" label="Company Code" v-model="dealer.CompanyCode" disabled/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <DatePicker label="Effective From" v-model="dealer.EffectiveFrom"/>
                  </div>
                  <div class="col">
                    <DatePicker label="Effective To"   v-model="dealer.EffectiveTo"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Website" label="Website" v-model="dealer.Website"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Address Line 1" label="Address Line 1" v-model="dealer.AddressLine1"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="Suite" label="Suite" v-model="dealer.AddressLine2"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <BasicTextInput placeholder="City" label="City" v-model="dealer.City"/>
                  </div>
                  <div class="col">
                    <StateSelect placeholder="" label="State" v-model="dealer.StateId"/>
                  </div>
                  <div class="col">
                    <ZipCodeInput placeholder="Zip" label="Zip" v-model="dealer.ZipCode"/>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-2 shadow-sm">
          <fieldset :disabled="!can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Sales</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <PercentInput placeholder="7.00%" label="Sales Tax" v-model="dealer.SalesTax"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Round To The Nearest Dollar" v-model="dealer.RoundToNearestDollar"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <DefaultFreightInput placeholder="7.00%" @isDefaultFreightPercentChanged="isDefaultFreightPercentChanged" :percentInput="dealer.IsDefaultFreightPercent" v-model="dealer.DefaultFreight"/>
                  </div>
                  <div class="col">
                    <Checkbox label="Tax Freight" v-model="dealer.TaxFreight"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-6">
                    <PercentInput placeholder="7.00%" label="GP" v-model="dealer.GPPercent"/>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-3 shadow-sm">
          <fieldset :disabled="adminView || !can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Configuration</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="OrderLink Enabled" v-model="dealer.OrderLinkEnabled"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Sales Rep Can See All Orders" v-model="dealer.SalesRepCanSeeAllOrders"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Sales Reps Share Customers" v-model="dealer.SalesRepShareCustomers"/>
                  </div>
                </div>
                <div class="row mb-2">
                  <div class="col">
                    <Checkbox label="Updates To Customers Allowed" v-model="dealer.UpdatesToCustomersAllowed"/>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-4 shadow-sm">
          <fieldset :disabled="adminView || !can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Primary Contact</h5>
              <hr>
            </div>
            <div class="card-body">
              <div class="container p-0 m-0">
                <div class="row">
                  <div class="col">
                    <b>Full Name:</b>
                  </div>
                  <div class="col right-justify-col">{{ dealer.FirstName }} {{ dealer.LastName }}</div>
                </div>
                <div class="row">
                  <div class="col">
                    <b>Email Address:</b>
                  </div>
                  <div class="col right-justify-col">{{ dealer.Email }}</div>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
        <div class="dealer-profile__card card card-5 shadow-sm">
          <div class="card-header">
            <h5 class="card-title">Manufacturers</h5>
            <hr>
          </div>
          <div class="card-body">
            <div class="card list-display-card p-0 m-0" style="overflow-y: scroll; -webkit-overflow-scrolling: touch;">
              <div v-for="(manufacturer,index) in dealer.Manufacturers" :key="index" class="row my-2">
                <div class="col list-item">
                  <i class="fa fa-circle" aria-hidden="true"></i><b class="spacing-left">{{manufacturer}}</b>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="dealer-profile__card card card-6 shadow-sm ">
          <fieldset :disabled="!can('edit-dealer-profile')">
            <div class="card-header">
              <h5 class="card-title">Upload Configurator</h5>
              <hr>
            </div>
            <input :key="configFileUpdateKey" :disabled="false" type="file" class="btn m-2" accept=".zip" @change="configuratorFileSelected">
            <div class="btn-wrapper">
              <button :disabled="false" type="button" @click="uploadConfiguratorDocument" class="btn btn-primary">Upload</button>
            </div>
          </fieldset> 
        </div>
      </div>
    </fieldset>
      <div v-if="adminView" class="dealer-profile__card card card-7 shadow-sm m-2">
        <div class="card-header">
          <h5 class="card-title">Upload Order Template</h5>
          <hr>
        </div>
        <input :key="fileUpdateKey" :disabled="false" type="file" class="btn m-2" accept=".pdf" @change="fileSelected">
        <div class="btn-wrapper">
          <button :disabled="false" type="button" @click="uploadDocument" class="btn btn-primary">Upload</button>
        </div>
      </div>
    <div  class="btn-wrapper">
      <!-- <button type="button" class="btn btn-secondary">Cancel</button> -->
      <div  @click="saveDealer" class="btn btn-primary">Save</div>
    </div>
  </div>
  
</template>

<script>
import moment              from 'moment';
import { mapState }        from 'vuex';

import { DealerService, DocumentService }      from '@/services';
import DefaultFreightInput from '../../../components/inputComponents/DefaultFreightInput';

import {
  getKeyByValue,
  dealerStatusEnum
}                          from "../../../modules/helpers";

export default {
  name: 'DealerProfile',
  components: {
    DefaultFreightInput
  },
  data: () => {
    return {
      dealer: {},
      file: null,
      fileUpdateKey: 0,
      configuratorFile: null,
      configFileUpdateKey: 0,
      dealerStatusOptions: [{text: 'Unregistered', value: 1}, {text: 'Registered', value: 2}, {text: 'Discontinued', value: 3}]
    }
  },
  computed:{
    ...mapState(['currentUser']),
    dealerStatus(){
      return getKeyByValue(dealerStatusEnum, this.dealer.DealerStatus);
    },
    dealerStatusStyle(){
      if (this.dealerStatus === 'Registered'  ) return 'badge-success';
      if (this.dealerStatus === 'Unregistered') return 'badge-warning';
      if (this.dealerStatus === 'Discontinued') return 'badge-danger';
    },
    adminView(){
      return this.$route.name === 'DealerProfile'
    }
  },
  methods: {
    uploadDocument(){
      DealerService.uploadTemplate({file: this.file, id: this.$route.params.id})
        .then(() => {
          this.file = null;
          this.fileUpdateKey++;
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Document Uploaded', type: 'success' });
        })
        .catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    uploadConfiguratorDocument(){
      DealerService.uploadConfigurator({file: this.configuratorFile})
        .then(() => {
          this.configuratorFile = null;
          this.configFileUpdateKey++;
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Document Uploaded', type: 'success' });
        })
        .catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    fileSelected(event){
      this.file = event.target.files[0]
    },
    configuratorFileSelected(event){
    
      this.configuratorFile = event.target.files[0]
    },
    isDefaultFreightPercentChanged(isDefPercent){
      this.dealer.IsDefaultFreightPercent = isDefPercent;
    },
    saveDealer(){
      DealerService.update(this.dealer.Id, this.dealer).then(res => {
          this.getDealer();
          this.$store.dispatch('ADD_NOTIFICATION', { text: 'Dealer Updated', type: 'success' });
        }).catch(err => {
          this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
        })
    },
    getDealer(){
      console.log('route', this.$route)
      let dealerId = this.adminView ? this.$route.params.id : this.currentUser.DealerId;
      // this.readOnly = 
      DealerService.get(dealerId).then(res => {
        this.dealer = res;
      }).catch(err => {
        this.$store.dispatch('ADD_NOTIFICATION', { text: err.response.data.Message  , type: 'error' });
      })
    }
  },

  created(){
    this.getDealer();
  }

}
</script>

<style scoped lang="scss">
  // .list-div{
  //   font-size: .5em;
  // }
  .dealer-profile-wrapper{
    // max-width: 1800px;
    min-height: 100%;
    .dealer-profile{
      
      .card-1 {
        grid-area: card-1;
      }
      .card-2 {
        grid-area: card-2;
      }
      .card-3 {
        grid-area: card-3;
      }
      .card-4 {
        grid-area: card-4;
      }
      .card-5 {
        grid-area: card-5;
      }
      .card-6{
        grid-area: card-6;
      }
      .card-7{
        grid-area: card-7;
      }
      height: 90%;
      display: grid;
      grid-template-columns: 50% 50%;
      grid-template-rows: auto;
      grid-template-areas: 
        "card-1 card-2"
        "card-1 card-2"
        "card-1 card-3"
        "card-4 card-5"
        "card-4 card-5"
        "card-6 card-6"
        "card-7 card-7";
      &__card{
        margin: 10px;
      }
    }
    .right-justify-col {
      display: flex;
      justify-content: flex-end;
    }
    .list-display-card {
      background-color: white;
      height: 100%;
      max-height: 200px;
      overflow: scroll;
      i {
        font-size: 12px;
      }
      .list-item {
        display: flex;
        font-size: .6em;
        justify-content: flex-start;
        align-items: center;
        margin-left: 10px;
        .spacing-left {
          margin-left: 10px;
        }
      }
    }
    .btn-wrapper{
      width: 100%;
      padding: 10px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .btn {
        margin: 5px;
      }
    }
  }
</style>
